import { scrubString } from "@/lib/sentryScrub"

/**
 * A loggable summary of a caught error that cannot carry the person's data.
 *
 * Sentry turns every console call into a breadcrumb, so a raw error object in
 * a log line ships with the next event. The panel's API client throws the
 * parsed response body, and a FastAPI 422 echoes the submitted values back
 * (`detail[].input`): logging it verbatim would send an email address, a
 * magic-link token or a secret's value. This keeps what is useful for
 * debugging (status, error type, which fields failed, the server's message
 * with emails and tokens scrubbed) and drops the rest.
 */
export function safeError(error: unknown): unknown {
  // Dev keeps the whole error for the browser console; Sentry never runs on
  // localhost.
  if (import.meta.env.DEV) return error
  return summarizeError(error)
}

/**
 * The summary itself, in every build. Never throws: it runs inside catch
 * blocks, where a throw would replace the error being handled.
 */
export function summarizeError(error: unknown): Record<string, unknown> {
  try {
    return summarize(error)
  } catch {
    return { type: "unloggable" }
  }
}

function summarize(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return { name: error.name, message: scrubString(error.message) }
  }
  if (!error || typeof error !== "object") {
    return { type: typeof error }
  }

  const body = error as Record<string, unknown>
  const summary: Record<string, unknown> = {}
  for (const key of ["status", "status_code", "code", "type"] as const) {
    const value = body[key]
    if (typeof value === "number") summary[key] = value
    if (typeof value === "string") summary[key] = scrubString(value).slice(0, 60)
  }

  const detail = body.detail
  if (Array.isArray(detail)) {
    // Validation errors: which fields and why, never the submitted value (nor
    // `msg`, which can quote it).
    summary.detail = detail.slice(0, 10).map((item) => {
      if (!item || typeof item !== "object") return typeof item
      const { loc, type } = item as { loc?: unknown; type?: unknown }
      return {
        loc: Array.isArray(loc) ? loc.map((p) => (typeof p === "number" ? p : String(p))) : undefined,
        type: typeof type === "string" ? type : undefined,
      }
    })
  } else if (typeof detail === "string") {
    // Server-authored messages, but a few interpolate an email: scrub them.
    summary.detail = scrubString(detail).slice(0, 300)
  } else if (detail && typeof detail === "object") {
    summary.detail = { keys: Object.keys(detail) }
  }

  summary.keys = Object.keys(body)
  return summary
}
